import { useEffect, useMemo, useState } from 'react';
import { authClient } from '@frontend/api-clients/authClient';
import { getEcosystems } from '@frontend/api-clients/ecosystemsClient';
import type { EcosystemDefinition, User } from '@frontend/types';
import {
  canSeeEcosystemAdmin,
  isPatriotAdmin,
  parseEcosystemAdminScopes,
} from '@frontend/utils/adminHubAccess';
import { useAdminWidgetExpanded } from './hooks/useAdminWidgetExpanded';
import EcosystemAdminScopePicker from './ecosystem/components/EcosystemAdminScopePicker';
import EcosystemIdentityPanel from './ecosystem/components/EcosystemIdentityPanel';
import EcosystemMembersPanel from './ecosystem/components/EcosystemMembersPanel';

interface EcosystemAdminPageProps {
  user: User | null;
}

export default function EcosystemAdminPage({ user }: EcosystemAdminPageProps) {
  const [ecosystemDefs, setEcosystemDefs] = useState<EcosystemDefinition[]>([]);
  const [selectedEcosystemId, setSelectedEcosystemId] = useState('');
  const { expanded, toggleWidget } = useAdminWidgetExpanded(user, 'ecosystem', {
    identity: true,
    members: true,
  });

  const scopedIds = useMemo(() => parseEcosystemAdminScopes(user), [user]);
  const patriot = isPatriotAdmin(user);

  const ecosystemOptions = useMemo(() => {
    const defs = patriot
      ? ecosystemDefs
      : ecosystemDefs.filter((d) => scopedIds.has(d.id) || user?.allowAllEcosystemAdmin);
    return defs.map((e) => ({ id: e.id, label: e.displayName || e.id }));
  }, [ecosystemDefs, patriot, scopedIds, user?.allowAllEcosystemAdmin]);

  useEffect(() => {
    if (!canSeeEcosystemAdmin(user)) return;
    const token = authClient.getToken();
    if (!token) return;
    void getEcosystems(token)
      .then((defs) => {
        setEcosystemDefs(defs);
        setSelectedEcosystemId((current) => current || defs[0]?.id || '');
      })
      .catch(() => {
        setEcosystemDefs([]);
      });
  }, [user]);

  useEffect(() => {
    if (
      ecosystemOptions.length > 0 &&
      !ecosystemOptions.some((o) => o.id === selectedEcosystemId)
    ) {
      setSelectedEcosystemId(ecosystemOptions[0].id);
    }
  }, [ecosystemOptions, selectedEcosystemId]);

  if (!canSeeEcosystemAdmin(user)) {
    return (
      <div className="tree-dashboard-panel">
        <h2>Ecosystem Admin</h2>
        <p className="tree-dashboard-muted">You do not have permission to view this section.</p>
      </div>
    );
  }

  const selectedLabel = ecosystemOptions.find((o) => o.id === selectedEcosystemId)?.label;

  return (
    <div className="tree-dashboard-panel">
      <h2>Ecosystem Admin</h2>
      <p className="tree-dashboard-muted">
        Create projects, manage members, and invite users within ecosystems you administer.
      </p>

      <EcosystemAdminScopePicker
        ecosystemOptions={ecosystemOptions}
        selectedEcosystemId={selectedEcosystemId}
        onSelect={setSelectedEcosystemId}
      />

      <EcosystemIdentityPanel
        expanded={Boolean(expanded.identity)}
        onToggle={() => toggleWidget('identity')}
        ecosystemId={selectedEcosystemId}
      />

      <EcosystemMembersPanel
        expanded={Boolean(expanded.members)}
        onToggle={() => toggleWidget('members')}
        user={user}
        ecosystemId={selectedEcosystemId}
        ecosystemLabel={selectedLabel}
      />
    </div>
  );
}
